// cspell:disable
import { Currency } from "@redotech/money/currencies";
import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  FreightProvider,
  FreightServiceLevel,
  KnownSCAC,
} from "@redotech/redo-fulfillment-carrier-common/freight";
import { FulfillmentOrderAddressSchema } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { freightBookingIdFormat } from "@redotech/redo-fulfillment-id";
import { surchargeSchema_ } from "@redotech/redo-fulfillment-outbound-labels-common/surcharge";
import { OutboundParcelSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { RateSchema } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { Provider } from "@redotech/redo-order-common/provider";
import { TrackingPlatform } from "@redotech/redo-order-tracking-tracker-common/tracking-platform";
import { privacy } from "@redotech/schema-privacy";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { DateFromDateOrIsoStringSchema } from "./date-schema";
import { RedoShopifyFulfillmentDisplayStatus } from "./fulfillment-display-status";
import { FulfillmentGroup } from "./fulfillment-group";
import {
  FulfillmentLineItemSchema,
  IFulfillmentLineItem,
} from "./fulfillment-line-item";
import { RedoShopifyFulfillmentStatus } from "./fulfillment-status";
import { BatchState, ScanFormStatus } from "./scan-form";

export enum OmsInsuranceProvider {
  EASYPOST = "easypost",
  SHIPSURANCE = "shipsurance",
}

export const RecordedShipmentSchema = z
  .object({
    shipmentDate: z
      .union([
        DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
        z.string().meta({ privacy: privacy.nonPii }),
      ])
      .nullish()
      .meta({ privacy: privacy.structured }),
    insuranceProvider: createEnum(OmsInsuranceProvider, {
      enumName: "OmsInsuranceProvider",
    }).meta({ privacy: privacy.nonPii }),
    shipmentId: z.string().meta({ privacy: privacy.nonPii }),
    trackingNumber: z.string().meta({ privacy: privacy.external_id }),
    recordSourceIdentifier: z
      .string()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    declaredValue: z.number().meta({ privacy: privacy.nonPii }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type RecordedShipment = z.infer<typeof RecordedShipmentSchema>;

export const ShipsuranceClaimSchema = z
  .object({
    claimId: z.string().meta({ privacy: privacy.nonPii }),
    claimRetrievalCode: z.string().meta({ privacy: privacy.null }),
    shipmentId: z.string().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export type ShipsuranceClaim = z.infer<typeof ShipsuranceClaimSchema>;
export interface IShipsuranceClaim extends z.ZodType<ShipsuranceClaim> {}

export const RedoFulfillmentSchema = z
  .object({
    provider: createEnum(Provider, { enumName: "Provider" }).meta({
      privacy: privacy.nonPii,
    }),
    externalId: z.string().meta({ privacy: privacy.nonPii }),
    name: z.string().meta({ privacy: privacy.nonPii }),
    createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    estimatedDeliveryAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    originAddress: FulfillmentOrderAddressSchema.meta({
      privacy: privacy.structured,
    }),
    destinationAddress: FulfillmentOrderAddressSchema.meta({
      privacy: privacy.structured,
    }),
    inTransitAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    deliveredAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    displayStatus: createEnum(RedoShopifyFulfillmentDisplayStatus, {
      enumName: "RedoShopifyFulfillmentDisplayStatus",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    status: createEnum(RedoShopifyFulfillmentStatus, {
      enumName: "RedoShopifyFulfillmentStatus",
    }).meta({ privacy: privacy.nonPii }),
    requiresShipping: z.boolean().meta({ privacy: privacy.nonPii }),
    totalQuantity: z.number().meta({ privacy: privacy.nonPii }),
    lineItems: z
      .array(
        (FulfillmentLineItemSchema as IFulfillmentLineItem).meta({
          privacy: privacy.structured,
        }),
      )
      .meta({ privacy: privacy.structured }),
    trackingInfo: z
      .array(
        z
          .object({
            company: z.string().meta({ privacy: privacy.nonPii }).nullish(),
            url: z.string().meta({ privacy: privacy.redact }).nullish(),
            number: z.string().meta({ privacy: privacy.external_id }).nullish(),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
    order: z
      .object({
        id: z.string().meta({ privacy: privacy.nonPii }),
        name: z.string().meta({ privacy: privacy.nonPii }),
        tags: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .nullish()
          .meta({ privacy: privacy.structured }),
        note: z.string().meta({ privacy: privacy.free_text }),
        createdAt: DateFromDateOrIsoStringSchema.meta({
          privacy: privacy.nonPii,
        }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    customer: z
      .object({
        id: z.string().meta({ privacy: privacy.nonPii }),
        name: z.string().meta({ privacy: privacy.person_name }),
        tags: z
          .array(z.string().meta({ privacy: privacy.nonPii }))
          .nullish()
          .meta({ privacy: privacy.structured }),
        numOrders: z.number().meta({ privacy: privacy.nonPii }).nullish(),
        email: z.string().meta({ privacy: privacy.email }).nullish(),
        phone: z.string().meta({ privacy: privacy.phone }).nullish(),
        note: z.string().meta({ privacy: privacy.free_text }).nullish(),
        localizations: z
          .array(z.string().meta({ privacy: privacy.null }))
          .nullish()
          .meta({ privacy: privacy.null }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    deleted: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    raw: z.any().meta({ privacy: privacy.null }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type RedoFulfillment = z.infer<typeof RedoFulfillmentSchema>;
export interface IRedoFulfillment extends z.ZodType<RedoFulfillment> {}

export const OutboundShipmentLabelSchema = z
  .object({
    url: z.string().meta({ privacy: privacy.redact }),
    printed: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).optional(),
    commercialInvoiceUrl: z
      .string()
      .meta({ privacy: privacy.redact })
      .optional()
      .nullish(),
    comInvoiceSubmittedElectronically: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    labelDate: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type OutboundShipmentLabel = z.infer<typeof OutboundShipmentLabelSchema>;

export const OutboundShipmentLineItemSchema = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }),
    quantity: z.number().meta({ privacy: privacy.nonPii }),
    sku: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type OutboundShipmentLineItem = z.infer<
  typeof OutboundShipmentLineItemSchema
>;

export const LineItemsByFulfillmentOrderSchema = z
  .array(
    z
      .object({
        fulfillmentOrderId: z.string().meta({ privacy: privacy.nonPii }),
        fulfillmentOrderLineItems: z
          .array(
            OutboundShipmentLineItemSchema.meta({
              privacy: privacy.structured,
            }),
          )
          .meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
  )
  .meta({ privacy: privacy.structured });
export type LineItemsByFulfillmentOrder = z.infer<
  typeof LineItemsByFulfillmentOrderSchema
>;

// https://support.easypost.com/hc/en-us/articles/360044353091-Tracking-Frequently-Asked-Questions#h_9656edcc-0b76-4851-8b09-a826d9add238
export enum ShipmentTrackerStatus {
  UNKNOWN = "unknown",
  PRE_TRANSIT = "pre_transit",
  IN_TRANSIT = "in_transit",
  OUT_FOR_DELIVERY = "out_for_delivery",
  AVAILABLE_FOR_PICKUP = "available_for_pickup",
  DELIVERED = "delivered",
  RETURN_TO_SENDER = "return_to_sender",
  FAILURE = "failure",
  CANCELLED = "cancelled",
  ERROR = "error",
  // Our own status for tracking shipments that are stuck in transit
  STALLED_IN_TRANSIT = "stalled_in_transit",
}

// TODO - we might need to revisit this - is "Cancelled" a terminal status?
export const TerminalOutboundTrackingStatuses = [
  ShipmentTrackerStatus.DELIVERED,
  ShipmentTrackerStatus.RETURN_TO_SENDER,
  ShipmentTrackerStatus.FAILURE,
  ShipmentTrackerStatus.CANCELLED,
  ShipmentTrackerStatus.ERROR,
];

// We rely on easypost webhooks for tracking syncs right now
export const TrackingPlatformsWeManuallySync = [
  TrackingPlatform.UPS,
  TrackingPlatform.FIRST_MILE,
  TrackingPlatform.EPOST_GLOBAL,
  TrackingPlatform.APC,
  TrackingPlatform.LANDMARK_GLOBAL,
  TrackingPlatform.GLOBALE,
  TrackingPlatform.WWEX,
  TrackingPlatform.DHL_ECS,
  TrackingPlatform.USPS,
  TrackingPlatform.UNIUNI,
  TrackingPlatform.STALLION_EXPRESS,
  TrackingPlatform.ECOM_EXPRESS,
  TrackingPlatform.FREIGHTWIRE,
  TrackingPlatform.AMAZON_BUY_SHIPPING,
  TrackingPlatform.CHITCHATS,
];

export const TrackingDetailSchema = z
  .object({
    carrier_code: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    datetime: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    description: z.string().meta({ privacy: privacy.free_text }).nullish(),
    message: z.string().meta({ privacy: privacy.free_text }),
    object: z.string().meta({ privacy: privacy.null }).nullish(),
    source: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    status: createEnum(ShipmentTrackerStatus, {
      enumName: "ShipmentTrackerStatus",
    }).meta({ privacy: privacy.nonPii }),
    status_detail: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    tracking_location: z
      .object({
        city: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        country: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        object: z.string().meta({ privacy: privacy.null }).nullish(),
        state: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        zip: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

export type ShipmentTrackerTrackingDetail = z.infer<
  typeof TrackingDetailSchema
>;

export const ShipmentTrackerSchema = z
  .object({
    id: z.string().meta({ privacy: privacy.external_id }),
    trackingPlatform: createEnum(TrackingPlatform, {
      enumName: "TrackingPlatform",
    })
      .default(TrackingPlatform.EASYPOST)
      .meta({ privacy: privacy.nonPii }),
    status: createEnum(ShipmentTrackerStatus, {
      enumName: "ShipmentTrackerStatus",
    }).meta({ privacy: privacy.nonPii }),
    trackingCode: z.string().meta({ privacy: privacy.external_id }),
    carrier: z.string().meta({ privacy: privacy.nonPii }),
    publicUrl: z.string().meta({ privacy: privacy.redact }),
    estDeliveryDate: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    trackingDetails: z
      .array(TrackingDetailSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    raw: z.any().meta({ privacy: privacy.null }).nullish(),
  })
  .meta({ privacy: privacy.structured });

export type OutboundShipmentTracker = z.infer<typeof ShipmentTrackerSchema>;

export const CanadaPostManifestSchema = z
  .object({
    manifestMode: createEnum(CanadaPostManifestMode, {
      enumName: "CanadaPostManifestMode",
    }).meta({ privacy: privacy.nonPii }),
    manifestRequired: z.boolean().meta({ privacy: privacy.nonPii }),
    groupId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    shipmentId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    trackingPin: z.string().meta({ privacy: privacy.external_id }).nullish(),
    manifestId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    manifestedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type CanadaPostManifest = z.infer<typeof CanadaPostManifestSchema>;

export const ShipmentPackageSchema_ = z
  .object({
    label: OutboundShipmentLabelSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    boughtRate: RateSchema.nullish().meta({ privacy: privacy.structured }),
    parcel: OutboundParcelSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    lineItemsByFulfillmentOrder: LineItemsByFulfillmentOrderSchema.meta({
      privacy: privacy.structured,
    }),
    slip: z
      .object({
        printed: DateFromDateOrIsoStringSchema.meta({
          privacy: privacy.nonPii,
        }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    labelSlipUrl: z.string().meta({ privacy: privacy.redact }).nullish(),
    scanFormId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    scanFormStatus: createEnum(ScanFormStatus, { enumName: "ScanFormStatus" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    scanFormBatch: z
      .object({
        id: z.string().meta({ privacy: privacy.nonPii }),
        state: createEnum(BatchState, { enumName: "BatchState" }).meta({
          privacy: privacy.nonPii,
        }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    voided: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    tracker: ShipmentTrackerSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    insured: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    insuredValue: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    merchantCarrierAccountLabelFee: z
      .number()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    canadaPostManifest: CanadaPostManifestSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    recordedShipment: RecordedShipmentSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    createdAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    updatedAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    shippingClaimIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    shipsuranceClaims: z
      .array(
        (ShipsuranceClaimSchema as IShipsuranceClaim).meta({
          privacy: privacy.structured,
        }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    surcharges: z
      .array(
        surchargeSchema_.and(
          z.object({
            balanceTransactionId: z.string().meta({ privacy: privacy.nonPii }),
          }),
        ),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const ShipmentPackageSchema: z.ZodType<ShipmentPackage> =
  ShipmentPackageSchema_;
export interface ShipmentPackage extends z.infer<
  typeof ShipmentPackageSchema_
> {}
export interface IShipmentPackage extends z.ZodType<ShipmentPackage> {}

// A shipment with no label and no bought rate was never actually purchased —
// failed buys can leave one behind, and treating it as bought strands the group.
export function isPurchasedShipment(
  shipment: ShipmentPackage | null | undefined,
): boolean {
  return !!(shipment?.label?.url || shipment?.boughtRate);
}

const FreightShipmentSchema_ = z
  .object({
    freightBookingId: freightBookingIdFormat.schema.meta({
      privacy: privacy.nonPii,
    }),
    billOfLadingUrl: z.string().meta({ privacy: privacy.redact }),
    scac: createEnum(KnownSCAC, { enumName: "KnownSCAC" }).meta({
      privacy: privacy.nonPii,
    }),
    serviceLevel: createEnum(FreightServiceLevel, {
      enumName: "FreightServiceLevel",
    }).meta({ privacy: privacy.nonPii }),
    quoteId: z.string().meta({ privacy: privacy.nonPii }),
    shipmentTransactionId: z.string().meta({ privacy: privacy.nonPii }),
    providerCost: z.string().meta({ privacy: privacy.nonPii }),
    cost: z.string().meta({ privacy: privacy.nonPii }),
    currency: createEnum(Currency, { enumName: "Currency" }).meta({
      privacy: privacy.nonPii,
    }),
    pickupDate: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    estDeliveryDays: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    bolNumber: z.string().meta({ privacy: privacy.nonPii }),
    trackingLink: z.string().meta({ privacy: privacy.redact }),
    pickupTransactionId: z.string().meta({ privacy: privacy.nonPii }),
    tracker: ShipmentTrackerSchema.meta({ privacy: privacy.structured }),
    provider: createEnum(FreightProvider, { enumName: "FreightProvider" }).meta(
      { privacy: privacy.nonPii },
    ),
    createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });
export const FreightShipmentSchema: z.ZodType<FreightShipment> =
  FreightShipmentSchema_;
export interface FreightShipment extends z.infer<
  typeof FreightShipmentSchema_
> {}

export const ShipmentDetailsSchema = z
  .object({
    createdAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    updatedAt: DateFromDateOrIsoStringSchema.meta({ privacy: privacy.nonPii }),
    voided: z.boolean().meta({ privacy: privacy.nonPii }),
    shipments: z
      .array(ShipmentPackageSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    freightShipment: FreightShipmentSchema.nullish().meta({
      privacy: privacy.structured,
    }),
  })
  .meta({ privacy: privacy.structured });

type GroupShipments = Pick<
  FulfillmentGroup,
  "shipment" | "additionalShipments" | "shipmentDetails"
>;

export function getFulfillmentGroupTrackingCodes(
  fulfillmentGroup: GroupShipments,
): string[] {
  const codes = [
    fulfillmentGroup.shipmentDetails?.freightShipment?.bolNumber,
    fulfillmentGroup.shipment?.tracker?.trackingCode,
    ...(fulfillmentGroup.additionalShipments ?? []).map(
      (s) => s.tracker?.trackingCode,
    ),
  ].filter((c): c is string => !!c);
  return Array.from(new Set(codes));
}

export function findSuccessFulfillmentsMatchingGroupTrackingCode<
  T extends Pick<RedoFulfillment, "status" | "trackingInfo">,
>(fulfillmentGroup: GroupShipments, fulfillments: T[]): T[] {
  const trackingCodes = getFulfillmentGroupTrackingCodes(fulfillmentGroup);
  if (trackingCodes.length === 0) {
    return [];
  }
  return fulfillments.filter(
    (f) =>
      f.status === RedoShopifyFulfillmentStatus.Success &&
      f.trackingInfo?.some(
        (t) => !!t.number && trackingCodes.includes(t.number),
      ),
  );
}

export function getSuccessFulfillmentsByTrackingCode(
  fulfillmentGroup: FulfillmentGroup,
): RedoFulfillment[] {
  const trackingCode =
    fulfillmentGroup.shipmentDetails?.freightShipment?.bolNumber ??
    fulfillmentGroup.shipment?.tracker?.trackingCode;
  if (!trackingCode) {
    throw new Error("No tracking code on shipment");
  }
  const fulfillments =
    fulfillmentGroup.fulfillments?.filter(
      (f) =>
        f.trackingInfo?.find((t) => t.number === trackingCode) &&
        f.status === RedoShopifyFulfillmentStatus.Success,
    ) ?? [];

  return fulfillments;
}

// https://shopify.dev/docs/api/admin-graphql/latest/objects/FulfillmentTrackingInfo#supported-tracking-companies
export function getShopifyFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }
  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "FedEx";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "Amazon";
    case Carriers.DHL_EXPRESS:
      return "DHL Express";
    case Carriers.VEHO:
      return "Veho";
    case Carriers.FIRST_MILE:
      return "FirstMile";
    case Carriers.DHL_ECS:
      return "DHL eCommerce";
    case Carriers.APC:
      return "APC";
    case Carriers.AUSTRALIA_POST:
      return "Australia Post";
    case Carriers.CANADA_POST:
      return "Canada Post";
    case Carriers.GLS:
      return "GLS";
    case Carriers.ASENDIA:
      return "Asendia";
    case Carriers.ROYAL_MAIL_V3:
      return "Royal Mail V3";
    case Carriers.SPEE_DEE:
      return "SpeeDee";
    case Carriers.PASSPORT_GLOBAL:
      return "Passport Global";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark Global";
    case Carriers.ETA_EXPRESS:
      return "ETA Express";
    case Carriers.EPOST_GLOBAL:
      return "ePost Global";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "Global-E";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS";
    case Carriers.UNIUNI:
      return "uniuni";
    case Carriers.PUROLATOR:
      return "Purolator";
    case Carriers.STALLION_EXPRESS:
      return "Stallion Express";
    case Carriers.ECOM_EXPRESS:
      return "Ecom Express";
    case Carriers.CHITCHATS:
      return "Chit Chats";
    case Carriers.POST_NL:
      return "PostNL";
    case Carriers.INTELCOM:
      return "Intelcom";
    case Carriers.FLEET_OPTICS:
      return "Fleet Optics";
    case Carriers.ICS_COURIER:
      return "ICS Courier";
    case Carriers.WHIZ_DELIVERY:
      return "Whiz Delivery";
    case Carriers.TIKTOK_SHOP:
      return "Tik Tok";
    case Carriers.CANPAR_EXPRESS:
      return "Canpar";
    case Carriers.GOFO:
      return "GOFO";
    case Carriers.P2P_GLOBAL:
      return "Point2Point Global";
    default:
      assertNever(carrier);
  }
}

// https://developer.bigcommerce.com/docs/rest-management/orders/order-shipments#create-order-shipment
export function getBigcommerceFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }
  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "fedex";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "ups";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "usps";
    case Carriers.AMAZON:
      return "";
    case Carriers.DHL_EXPRESS:
      return "";
    case Carriers.VEHO:
      return "";
    case Carriers.FIRST_MILE:
      return "";
    case Carriers.DHL_ECS:
      return "";
    case Carriers.APC:
      return "";
    case Carriers.AUSTRALIA_POST:
      return "";
    case Carriers.CANADA_POST:
      return "";
    case Carriers.GLS:
      return "";
    case Carriers.ASENDIA:
      return "";
    case Carriers.ROYAL_MAIL_V3:
      return "";
    case Carriers.SPEE_DEE:
      return "";
    case Carriers.PASSPORT_GLOBAL:
      return "";
    case Carriers.LANDMARK_GLOBAL:
      return "";
    case Carriers.ETA_EXPRESS:
      return "ETA Express";
    case Carriers.EPOST_GLOBAL:
      return "";
    case Carriers.ONTRAC:
      return "";
    case Carriers.GLOBALE:
      return "";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "ups";
    case Carriers.UNIUNI:
      return "";
    case Carriers.PUROLATOR:
      return "";
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return "";
    case Carriers.CANPAR_EXPRESS:
    case Carriers.GOFO:
    case Carriers.P2P_GLOBAL:
      return "";
    default:
      assertNever(carrier);
  }
}

// TikTok returns carrier/service names with trailing dots ("UPS.", "FedEx..")
// and trademark symbols ("UPS®", "FedEx Ground® Economy") that break exact and
// substring matching against our names.
export function normalizeTiktokShippingName(name: string): string {
  return name.replace(/[®™]/g, "").replace(/\.+$/, "").trim();
}

// https://partner.tiktokshop.com/docv2/page/650aa48d4a0bb702c06d85cd?external_id=650aa48d4a0bb702c06d85cd
export function getTiktokFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }

  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "FedEx";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "Amazon shipping + Amazon MCF";
    case Carriers.DHL_EXPRESS:
      return "DHL express";
    case Carriers.VEHO:
      return "Veho";
    case Carriers.FIRST_MILE:
      return "First Mile";
    case Carriers.DHL_ECS:
      return "DHL eCommerce";
    case Carriers.APC:
      return "";
    case Carriers.AUSTRALIA_POST:
      return "";
    case Carriers.CANADA_POST:
      return "";
    case Carriers.GLS:
      return "GLS US";
    case Carriers.ASENDIA:
      return "Asendia US";
    case Carriers.ROYAL_MAIL_V3:
      return "";
    case Carriers.SPEE_DEE:
      return "Spee-Dee Delivery";
    case Carriers.PASSPORT_GLOBAL:
      return "";
    case Carriers.LANDMARK_GLOBAL:
      return "";
    case Carriers.ETA_EXPRESS:
      return "";
    case Carriers.EPOST_GLOBAL:
      return "";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return "";
    case Carriers.STALLION_EXPRESS:
      return "Stallion Express";
    case Carriers.ECOM_EXPRESS:
      return "";
    case Carriers.CHITCHATS:
      return "Chit Chats";
    case Carriers.POST_NL:
      return "PostNL";
    case Carriers.INTELCOM:
      return "Intelcom";
    case Carriers.FLEET_OPTICS:
      return "Fleet Optics";
    case Carriers.ICS_COURIER:
      return "ICS Courier";
    case Carriers.WHIZ_DELIVERY:
      return "Whiz Delivery";
    case Carriers.TIKTOK_SHOP:
      return "TikTok";
    case Carriers.CANPAR_EXPRESS:
    case Carriers.GOFO:
    case Carriers.P2P_GLOBAL:
      return "";
    default:
      assertNever(carrier);
  }
}

// https://developer.ebay.com/devzone/XML/docs/Reference/eBay/types/ShippingCarrierCodeType.html
export function getEbayFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }

  switch (carrier) {
    case Carriers.FEDEX:
      return "FedEx";
    case Carriers.FEDEX_SMART_POST:
      return "FedExSmartPost";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "";
    case Carriers.DHL_EXPRESS:
      return "DHL";
    case Carriers.VEHO:
      return "";
    case Carriers.FIRST_MILE:
      return "";
    case Carriers.DHL_ECS:
      return "DHLGlobalMail"; // Global mail was rebranded to dhl ecommerce in 2014
    case Carriers.APC:
      return "APC";
    case Carriers.AUSTRALIA_POST:
      return "AustraliaPost";
    case Carriers.CANADA_POST:
      return "CanadaPost";
    case Carriers.GLS:
      return "GLS";
    case Carriers.ASENDIA:
      return "";
    case Carriers.ROYAL_MAIL_V3:
      return "Royal Mail V3";
    case Carriers.SPEE_DEE:
      return "SpeeDee";
    case Carriers.PASSPORT_GLOBAL:
      return "Passport Global";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark";
    case Carriers.ETA_EXPRESS:
      return "ETA Express";
    case Carriers.EPOST_GLOBAL:
      return "ePost Global";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS Mail Innovations";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return "";
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return "";
    case Carriers.CANPAR_EXPRESS:
    case Carriers.GOFO:
    case Carriers.P2P_GLOBAL:
      return "";
    default:
      assertNever(carrier);
  }
}

// https://developer.walmart.com/api/us/mp/orders#operation/shipOrderLines
export function getWalmartFriendlyCarrierName(
  carrier: Carriers | undefined,
): string | undefined {
  if (!carrier) {
    return undefined;
  }

  switch (carrier) {
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_RETURNS:
    case Carriers.USPS_SHIP:
      return "USPS";
    case Carriers.FEDEX:
      return "FedEx";
    case Carriers.FEDEX_SMART_POST:
      return "FEDEXSP";
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
      return "DHL Ecommerce - US";
    case Carriers.FIRST_MILE:
      return "FIRST MILE";
    case Carriers.GLS:
      return "GLS";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark Global";
    case Carriers.AMAZON:
    case Carriers.VEHO:
    case Carriers.AUSTRALIA_POST:
    case Carriers.CANADA_POST:
    case Carriers.APC:
    case Carriers.ASENDIA:
    case Carriers.ROYAL_MAIL_V3:
    case Carriers.SPEE_DEE:
    case Carriers.PASSPORT_GLOBAL:
    case Carriers.ETA_EXPRESS:
      return undefined;
    case Carriers.EPOST_GLOBAL:
      return undefined;
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return undefined;
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPSMI";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return undefined;
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return undefined;
    case Carriers.CANPAR_EXPRESS:
    case Carriers.GOFO:
    case Carriers.P2P_GLOBAL:
      return undefined;
    default:
      assertNever(carrier);
  }
}

// https://www.wayfair.com/supplier/home/documentation/dropship-supplier-api-docs
// Returns the SCAC carrier code expected by the Wayfair shipment API.
export function getWayfairScacCarrierCode(
  carrier: Carriers | undefined,
): string | undefined {
  if (!carrier) {
    return undefined;
  }

  switch (carrier) {
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPSN";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.FEDEX:
      return "FDEG";
    case Carriers.FEDEX_SMART_POST:
      return "FDXE";
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
      return "DHLW";
    case Carriers.AMAZON:
    case Carriers.VEHO:
    case Carriers.FIRST_MILE:
    case Carriers.APC:
    case Carriers.AUSTRALIA_POST:
    case Carriers.CANADA_POST:
    case Carriers.GLS:
    case Carriers.ASENDIA:
    case Carriers.ROYAL_MAIL_V3:
    case Carriers.SPEE_DEE:
    case Carriers.PASSPORT_GLOBAL:
    case Carriers.LANDMARK_GLOBAL:
    case Carriers.ETA_EXPRESS:
    case Carriers.EPOST_GLOBAL:
    case Carriers.ONTRAC:
    case Carriers.GLOBALE:
    case Carriers.PUROLATOR:
    case Carriers.UNIUNI:
    case Carriers.STALLION_EXPRESS:
    case Carriers.ECOM_EXPRESS:
    case Carriers.CHITCHATS:
    case Carriers.POST_NL:
    case Carriers.INTELCOM:
    case Carriers.FLEET_OPTICS:
    case Carriers.ICS_COURIER:
    case Carriers.WHIZ_DELIVERY:
    case Carriers.TIKTOK_SHOP:
      return undefined;
    case Carriers.CANPAR_EXPRESS:
    case Carriers.GOFO:
    case Carriers.P2P_GLOBAL:
      return undefined;
    default:
      assertNever(carrier);
  }
}

// CS carriers must be at most 10 characters
export function getCommentsoldFriendlyCarrierName(
  carrier: Carriers | undefined,
): string {
  if (!carrier) {
    return "";
  }

  switch (carrier) {
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return "FedEx";
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return "UPS";
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return "USPS";
    case Carriers.AMAZON:
      return "Amazon";
    case Carriers.DHL_EXPRESS:
    case Carriers.DHL_ECS:
      return "DHL";
    case Carriers.VEHO:
      return "Veho";
    case Carriers.FIRST_MILE:
      return "FirstMile";
    case Carriers.APC:
      return "APC";
    case Carriers.AUSTRALIA_POST:
      return "Aus Post";
    case Carriers.CANADA_POST:
      return "CanadaPost";
    case Carriers.GLS:
      return "GLS";
    case Carriers.ASENDIA:
      return "Asendia";
    case Carriers.ROYAL_MAIL_V3:
      return "Royal Mail";
    case Carriers.SPEE_DEE:
      return "SpeeDee";
    case Carriers.PASSPORT_GLOBAL:
      return "Passport";
    case Carriers.LANDMARK_GLOBAL:
      return "Landmark";
    case Carriers.ETA_EXPRESS:
      return "ETA";
    case Carriers.EPOST_GLOBAL:
      return "ePost";
    case Carriers.ONTRAC:
      return "OnTrac";
    case Carriers.GLOBALE:
      return "Global-E";
    case Carriers.UPS_MAIL_INNOVATIONS:
      return "UPS MI";
    case Carriers.UNIUNI:
      return "UniUni";
    case Carriers.PUROLATOR:
      return "Purolator";
    case Carriers.STALLION_EXPRESS:
      return "Stallion";
    case Carriers.ECOM_EXPRESS:
      return "Ecom";
    case Carriers.CHITCHATS:
      return "Chit Chats";
    case Carriers.POST_NL:
      return "PostNL";
    case Carriers.INTELCOM:
      return "Intelcom";
    case Carriers.FLEET_OPTICS:
      return "FleetOptics";
    case Carriers.ICS_COURIER:
      return "ICS";
    case Carriers.WHIZ_DELIVERY:
      return "WhizDelivery";
    case Carriers.TIKTOK_SHOP:
      return "Tik Tok";
    case Carriers.CANPAR_EXPRESS:
      return "Canpar";
    case Carriers.GOFO:
      return "GOFO";
    case Carriers.P2P_GLOBAL:
      return "P2P";
    default:
      assertNever(carrier);
  }
}

// https://developers.faire.com/docs#/schemas/ExternalShipmentV2
export enum FaireCarrier {
  CANADA_POST = "CANADA_POST",
  DHL_ECOMMERCE = "DHL_ECOMMERCE",
  DHL_EXPRESS = "DHL_EXPRESS",
  FEDEX = "FEDEX",
  PUROLATOR = "PUROLATOR",
  UPS = "UPS",
  USPS = "USPS",
  POSTNL = "POSTNL",
  CANPAR = "CANPAR",
  INTERLINK_EXPRESS = "INTERLINK_EXPRESS",
  GSO = "GSO",
  ROYAL_MAIL = "ROYAL_MAIL",
  DPD = "DPD",
  DPDUK = "DPDUK",
  PARCELFORCE = "PARCELFORCE",
  AUSTRALIA_POST = "AUSTRALIA_POST",
  EVRI = "EVRI",
  LA_POSTE = "LA_POSTE",
}

export function mapRedoCarrierToFaire(redoCarrier: Carriers): string {
  switch (redoCarrier) {
    case Carriers.CANADA_POST:
      return FaireCarrier.CANADA_POST;
    case Carriers.DHL_ECS:
      return FaireCarrier.DHL_ECOMMERCE;
    case Carriers.DHL_EXPRESS:
      return FaireCarrier.DHL_EXPRESS;
    case Carriers.FEDEX:
    case Carriers.FEDEX_SMART_POST:
      return FaireCarrier.FEDEX;
    case Carriers.UPS:
    case Carriers.UPS_SUREPOST:
      return FaireCarrier.UPS;
    case Carriers.USPS:
    case Carriers.USPS_SHIP:
    case Carriers.USPS_RETURNS:
      return FaireCarrier.USPS;
    case Carriers.ROYAL_MAIL_V3:
      return FaireCarrier.ROYAL_MAIL;
    case Carriers.AUSTRALIA_POST:
      return FaireCarrier.AUSTRALIA_POST;
  }

  return redoCarrier;
}
